import React, { useState, useMemo } from "react";
import {
  Button,
  Flex,
  Typography,
  Tag,
  Space,
  Table,
  Input,
  Alert,
} from "antd";
import {
  UploadOutlined,
  DownloadOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  useGetWsProductsQuery,
  WsProduct,
} from "../../../api/webshop/wsProductsApi";
import { ColumnsType } from "antd/es/table";
import EditProductModal from "./EditProductModal";
import WebshopUploadsPage from "./UploadHistoryPage";
import { WsProductExtended } from "./VariantTransferTab";
import BulkOperations from "./BulkOperations";
import ExportProductsModal from "./ExportProductsModal";

const { Title, Text } = Typography;

const newProductTemplate: WsProductExtended = {
  Id: "",
  productId: "",
  title: "",
  productTitle: "",
  description: "",
  imageUrl: "",
  status: "ACTIVE",
  coverageCountries: [],
  variantIds: [],
};

export default function WebshopProductsPage() {
  const {
    data: productsData,
    isLoading,
    error,
    refetch,
  } = useGetWsProductsQuery();
  const [editProduct, setEditProduct] = useState<WsProductExtended>();
  const [showUploads, setShowUploads] = useState(false);
  const [showBulkOps, setShowBulkOps] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const columns: ColumnsType<WsProduct> = [
    {
      key: "title",
      title: "Product Title / Region",
      dataIndex: "title",
    },
    {
      key: "simType",
      title: "Sim Type",
      dataIndex: "simType",
    },
    {
      key: "variantCount",
      title: "# of products",
      dataIndex: "variantCount",
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "status",
      render: (status: string) => {
        const color = status === "ACTIVE" ? "green" : "red";
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      key: "action",
      title: "Action",
      render: (_: any, rec: WsProduct) => (
        <Button onClick={() => setEditProduct(rec)}>Edit</Button>
      ),
    },
  ];

  function handleCreate() {
    setEditProduct({ ...newProductTemplate });
  }

  function handleClose() {
    setEditProduct(undefined);
  }

  function searchFilter(product: WsProduct, s: string): boolean {
    const titleMatch =
      product.title?.toLowerCase().includes(s.toLowerCase()) ?? false;
    const idMatch =
      product.productId?.toLowerCase().includes(s.toLowerCase()) ?? false;
    return titleMatch || idMatch;
  }

  const filteredProducts = useMemo(() => {
    if (!productsData) return [];
    const products = !searchQuery.trim()
      ? productsData
      : productsData.filter((product) =>
          searchFilter(product, searchQuery.trim()),
        );
    return [...products].sort((a, b) =>
      (a.productTitle ?? "").localeCompare(b.productTitle ?? "", undefined, {
        sensitivity: "base",
      }),
    );
  }, [productsData, searchQuery]);

  const isCreating = editProduct && !editProduct.productId;

  if (showUploads) {
    return (
      <WebshopUploadsPage
        onBack={() => setShowUploads(false)}
        onUploadSuccess={refetch}
      />
    );
  }

  return (
    <>
      <Flex gap={16} vertical style={{ width: "100%" }}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <div>
            <Title level={2} style={{ margin: 0 }}>
              Webshop Products Management
            </Title>
            <Text type="secondary">
              Manage webshop products, upload CSV/Excel files, and monitor
              upload jobs.
            </Text>
          </div>
          <Space wrap>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => setShowExport(true)}
              style={{
                backgroundColor: "rgb(255, 70, 37)",
                borderColor: "rgb(255, 70, 37)",
              }}
            >
              Export Products
            </Button>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => setShowUploads(true)}
              style={{
                backgroundColor: "rgb(255, 70, 37)",
                borderColor: "rgb(255, 70, 37)",
              }}
            >
              Upload Products
            </Button>
            <Button
              type="primary"
              onClick={() => setShowBulkOps(true)}
              style={{
                backgroundColor: "rgb(255, 70, 37)",
                borderColor: "rgb(255, 70, 37)",
              }}
            >
              Bulk Operations
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
              style={{
                backgroundColor: "rgb(255, 70, 37)",
                borderColor: "rgb(255, 70, 37)",
              }}
            >
              New Product
            </Button>
          </Space>
        </Flex>

        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <Input.Search
            placeholder="Search products by title or ID..."
            allowClear
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ maxWidth: 360 }}
          />
          {productsData && (
            <Text type="secondary">
              Showing {filteredProducts.length} of {productsData.length}{" "}
              products
            </Text>
          )}
        </Flex>

        {error && (
          <Alert
            message="Failed to load webshop products"
            description="An error occurred while fetching the webshop products list. Please try refetching."
            type="error"
            showIcon
            action={
              <Button size="small" danger onClick={refetch}>
                Retry
              </Button>
            }
          />
        )}

        <Table
          dataSource={filteredProducts}
          columns={columns}
          loading={isLoading}
          size="small"
          rowKey={(record: any) => record.Id + record.variantId}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          style={{
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            borderRadius: "8px",
            overflow: "hidden",
            backgroundColor: "#fff",
          }}
        />

        <EditProductModal
          title={isCreating ? "Create Webshop Product" : "Edit Webshop Product"}
          pg={editProduct}
          setPg={setEditProduct}
          onClose={handleClose}
        />

        <BulkOperations
          open={showBulkOps}
          onClose={() => setShowBulkOps(false)}
          products={productsData || []}
        />

        <ExportProductsModal
          open={showExport}
          onClose={() => setShowExport(false)}
          products={productsData || []}
        />
      </Flex>
    </>
  );
}
